import React, { useContext } from 'react';
import {
  Box,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ContactSchema } from '../../definitions/schemas';
import { getDisplayName } from '../../lib/DisplayName';
import { getCrmConnectedData } from '../../lib/helper/IntegrationsHelper';
import { IntegrationsContext } from '../Integrations/IntegrationsContext';
import GuestAvatar, {
  AvatarBackground,
  AvatarColor,
  GuestAvatarProps,
} from '../Guest/GuestAvatar';

const useStyles = makeStyles(() => ({
  listItemSecondaryAction: {
    top: 0,
    transform: 'initial',
  },
}));

type Props = {
  action?: React.ReactNode;
  background?: AvatarBackground;
  contact: ContactSchema;
  ContactAvatarProps?: Partial<GuestAvatarProps>;
  disabled?: boolean;
  disableGutters?: boolean;
  displayJobTitle?: boolean;
  displayMobilePhone?: boolean;
  fullWidth?: boolean;
  nameIcon?: React.ReactNode;
  small?: boolean;
};

const ContactDetails: React.FC<Props> = (props: Props): JSX.Element => {
  const { installedIntegrations } = useContext(IntegrationsContext);
  const classes = useStyles();

  const {
    action,
    background = AvatarBackground.Secondary,
    contact,
    ContactAvatarProps,
    disabled = false,
    disableGutters = false,
    displayJobTitle = false,
    displayMobilePhone = false,
    fullWidth = false,
    nameIcon,
    small = false,
  } = props;

  const [, CrmIcon] = getCrmConnectedData(installedIntegrations);

  let secondaryText = contact.email;
  if (displayJobTitle && contact.jobTitle) {
    secondaryText = contact.jobTitle;
  }
  if (displayMobilePhone && contact.mobilePhone) {
    secondaryText = contact.mobilePhone;
  }

  const renderIcon = () => {
    if (!CrmIcon) {
      return undefined;
    }

    return <CrmIcon width={16} height={18} />;
  };

  const renderName = () => {
    let icon = contact.crmId ? renderIcon() : nameIcon;
    if (icon) {
      icon = (
        <Box component="span" ml={1}>
          {icon}
        </Box>
      );
    }

    const fullName = getDisplayName(contact);
    const MAX_NAME_LENGTH = 25;
    const isTruncated = fullName.length > MAX_NAME_LENGTH;
    const displayName = isTruncated
      ? `${fullName.slice(0, MAX_NAME_LENGTH)}...`
      : fullName;

    return (
      <Tooltip
        title={isTruncated ? fullName : ''}
        placement="top"
        PopperProps={{ disablePortal: true }}
      >
        <Box component="span" display="flex">
          {displayName}
          {icon}
        </Box>
      </Tooltip>
    );
  };

  return (
    <ListItem
      alignItems="center"
      component="div"
      ContainerComponent="div"
      ContainerProps={{ style: { width: fullWidth ? '100%' : undefined } }}
      disableGutters={disableGutters}
    >
      <ListItemAvatar>
        <GuestAvatar
          background={background}
          color={
            background !== AvatarBackground.Custom
              ? AvatarColor.Secondary
              : undefined
          }
          guest={contact}
          {...ContactAvatarProps}
        />
      </ListItemAvatar>
      <ListItemText
        primary={renderName()}
        primaryTypographyProps={{
          sx: { color: disabled ? 'text.disabled' : undefined },
          variant: small ? 'body2' : 'body1',
        }}
        secondary={secondaryText}
        secondaryTypographyProps={{
          noWrap: true,
          sx: { color: disabled ? 'text.disabled' : undefined },
          variant: small ? 'caption' : 'body2',
        }}
      />
      {action && (
        <ListItemSecondaryAction
          classes={{ root: classes.listItemSecondaryAction }}
        >
          {action}
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

export default ContactDetails;
